import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PlatformSite, PlatformStaff, PlatformTenant, PlatformTenantDirectory, ServerApiClient } from '../../data/ServerApiClient'
import './PlatformTenantsPage.css'

type PlatformApi = Pick<ServerApiClient,
  'listPlatformTenants' | 'grantPlatformSiteAccess' | 'revokePlatformSiteAccess' |
  'listPlatformStaff' | 'grantPlatformStaff' | 'revokePlatformStaff' | 'extendPlatformTenantTrial'
>

function todayLocal(): string {
  const date = new Date()
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function trialDate(value: string | null): string {
  if (!value) return 'No app trial'
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return 'Trial date unavailable'
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function extendedTrialEnd(tenant: PlatformTenant, days: number): string {
  const currentEnd = Date.parse(tenant.trial_ends_at || '')
  return new Date(Math.max(Date.now(), Number.isFinite(currentEnd) ? currentEnd : 0) + days * 86_400_000).toISOString()
}

function localDateTime(value: string): string {
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

const cardStyle = { border: '1px solid var(--fg-border-2)', borderRadius: 10, background: 'var(--fg-surface)', padding: 16 }
const buttonStyle = { border: '1px solid var(--fg-shell-border)', borderRadius: 7, padding: '7px 11px', background: 'var(--fg-surface)', color: 'var(--fg-shell-text)', fontWeight: 700 as const, cursor: 'pointer' }

export function PlatformTenantsPage({ api, role, onBack, onOpenSite, onAccessChanged }: {
  api: PlatformApi
  role: 'founder' | 'admin' | 'sales'
  onBack: () => void
  onOpenSite: (slug: string) => void
  onAccessChanged: () => Promise<void>
}) {
  const [directory, setDirectory] = useState<PlatformTenantDirectory | null>(null)
  const [staff, setStaff] = useState<PlatformStaff[]>([])
  const [search, setSearch] = useState('')
  const [selectedSite, setSelectedSite] = useState<PlatformSite | null>(null)
  const [approvedBy, setApprovedBy] = useState('')
  const [approvalDate, setApprovalDate] = useState(todayLocal)
  const [approvalNote, setApprovalNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [staffEmail, setStaffEmail] = useState('')
  const [staffRole, setStaffRole] = useState<'founder' | 'sales' | 'admin'>('sales')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [selectedTrialTenant, setSelectedTrialTenant] = useState<PlatformTenant | null>(null)
  const [trialEnd, setTrialEnd] = useState('')
  const [trialPreset, setTrialPreset] = useState<number | null>(14)
  const [trialReason, setTrialReason] = useState('')
  const [trialError, setTrialError] = useState('')
  const [trialSuccess, setTrialSuccess] = useState('')
  const trialBusyRef = useRef(false)
  const trialDialogRef = useRef<HTMLDialogElement>(null)
  const trialReturnFocusRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!selectedTrialTenant) return
    const dialog = trialDialogRef.current
    if (!dialog) return
    dialog.showModal()
    dialog.querySelector<HTMLTextAreaElement>('textarea')?.focus()
    return () => {
      if (dialog.open) dialog.close()
    }
  }, [selectedTrialTenant])

  useEffect(() => {
    if (selectedTrialTenant || busy) return
    const returnFocus = trialReturnFocusRef.current
    trialReturnFocusRef.current = null
    if (returnFocus?.isConnected) returnFocus.focus()
  }, [selectedTrialTenant, busy])

  const load = useCallback(async () => {
    const next = await api.listPlatformTenants()
    setDirectory(next)
    if (role === 'founder') setStaff(await api.listPlatformStaff())
  }, [api, role])

  useEffect(() => {
    void load().catch((err) => setError(String(err?.message || err || 'Could not load tenant directory.')))
  }, [load])

  const visibleTenants = useMemo(() => {
    if (!directory) return []
    const term = search.trim().toLowerCase()
    return directory.tenants
      .map((tenant) => ({
        tenant,
        sites: directory.sites.filter((site) => site.tenant_id === tenant.id),
      }))
      .filter(({ tenant, sites }) => !term ||
        [tenant.name, tenant.slug, ...tenant.admin_emails, ...sites.flatMap((site) => [site.name, site.slug])]
          .some((value) => value.toLowerCase().includes(term)))
  }, [directory, search])

  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    setError('')
    try {
      await action()
      await load()
    } catch (err: any) {
      setError(String(err?.message || err || 'Action failed.'))
    } finally {
      setBusy(false)
    }
  }

  const grant = async () => {
    if (!selectedSite || !confirmed || !approvedBy.trim() || !approvalDate) return
    await run(async () => {
      await api.grantPlatformSiteAccess(selectedSite.id, {
        approved_by_name: approvedBy.trim(),
        approval_received_on: approvalDate,
        approval_note: approvalNote.trim() || undefined,
        confirm_verbal_approval: true,
      })
      setSelectedSite(null)
      setApprovedBy('')
      setApprovalDate(todayLocal())
      setApprovalNote('')
      setConfirmed(false)
      await onAccessChanged()
    })
  }

  const openTrialExtension = (tenant: PlatformTenant) => {
    setSelectedTrialTenant(tenant)
    setTrialEnd(extendedTrialEnd(tenant, 14))
    setTrialPreset(14)
    setTrialReason('')
    setTrialError('')
    setTrialSuccess('')
  }

  const trialEndMs = Date.parse(trialEnd)
  const existingTrialEndMs = Date.parse(selectedTrialTenant?.trial_ends_at || '')
  const trialEndValid = Number.isFinite(trialEndMs) && trialEndMs > Math.max(Date.now(), existingTrialEndMs || 0)
    && trialEndMs <= Date.now() + 365 * 86_400_000

  const extendTrial = async () => {
    if (trialBusyRef.current || busy || !selectedTrialTenant || !trialEndValid || !trialReason.trim() || trialReason.trim().length > 2000) return
    trialBusyRef.current = true
    setBusy(true)
    setTrialError('')
    try {
      const result = await api.extendPlatformTenantTrial(selectedTrialTenant.id, {
        trial_ends_at: trialEnd,
        expected_trial_ends_at: selectedTrialTenant.trial_ends_at,
        reason: trialReason.trim(),
      })
      setTrialSuccess(`${selectedTrialTenant.name}’s trial now ends ${trialDate(result.trial_ends_at)}.`)
      setSelectedTrialTenant(null)
      try {
        await load()
      } catch {
        setError('The trial was extended, but the directory could not reload. Reload this page to see the updated date.')
      }
    } catch (err: any) {
      setTrialError(String(err?.message || err || 'Could not extend this trial.'))
    } finally {
      trialBusyRef.current = false
      setBusy(false)
    }
  }

  return (
    <main style={{ padding: 20, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26 }}>All tenants</h1>
          <p style={{ margin: '5px 0 0', color: 'var(--fg-muted)' }}>
            {role === 'founder' ? 'Founder' : role === 'admin' ? 'Platform admin' : 'Sales'} directory · Site access requires owner approval.
          </p>
        </div>
        <button type="button" style={buttonStyle} onClick={onBack}>Back</button>
      </div>
      {error ? <div role="alert" style={{ ...cardStyle, border: '1px solid #c0492f', color: '#a83e29' }}>{error}</div> : null}
      {trialSuccess ? <div role="status" style={{ ...cardStyle, color: 'var(--fg-shell-text)' }}>{trialSuccess}</div> : null}
      <label style={{ display: 'grid', gap: 5, maxWidth: 440, fontWeight: 700 }}>
        Find a tenant, site, or admin
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search all tenants" style={{ padding: 9 }} />
      </label>
      {!directory ? <p>Loading tenants…</p> : <p style={{ margin: 0, color: 'var(--fg-muted)' }}>{visibleTenants.length} tenants · {directory.sites.length} sites total</p>}
      {visibleTenants.map(({ tenant, sites }) => (
        <section key={tenant.id} style={{ ...cardStyle, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 19 }}>{tenant.name}</h2>
              <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{tenant.slug} · {sites.length} {sites.length === 1 ? 'site' : 'sites'}</div>
              <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Tenant admins: {tenant.admin_emails.length ? tenant.admin_emails.join(', ') : 'None listed'}</div>
              <div style={{ color: 'var(--fg-muted)', fontSize: 13, marginTop: 6 }}>
                {tenant.has_subscription
                  ? `Subscription${tenant.plan_status ? ` · ${tenant.plan_status}` : ''}. Trial and payment dates are managed in billing.`
                  : tenant.trial_ends_at ? `App trial ends ${trialDate(tenant.trial_ends_at)}` : 'No app trial recorded.'}
              </div>
            </div>
            {role !== 'sales' && tenant.can_extend_trial && !tenant.has_subscription ? (
              <button type="button" aria-haspopup="dialog" disabled={busy} style={buttonStyle} onClick={(event) => { trialReturnFocusRef.current = event.currentTarget; openTrialExtension(tenant) }}>Extend trial</button>
            ) : null}
          </div>
          {sites.map((site) => {
            const hasAccess = site.current_site_status === 'active' && site.current_site_role !== 'unlicensed'
            const hasAdminAccess = hasAccess && ['site_admin_creator', 'site_admin_explorer'].includes(site.current_site_role || '')
            return (
              <div key={site.id} style={{ borderTop: '1px solid var(--fg-border-2)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <strong>{site.name}</strong> <span style={{ color: 'var(--fg-muted)' }}>/{site.slug}</span>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 12 }}>{site.user_count} users · {site.project_count} projects{hasAccess ? ` · Your role: ${site.current_site_role}` : ''}</div>
                  {site.platform_grant_id && site.approval_by ? <div style={{ color: 'var(--fg-muted)', fontSize: 12 }}>Verbal approval: {site.approval_by}, {site.approval_received_on}</div> : null}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {hasAccess ? <button type="button" style={buttonStyle} onClick={() => onOpenSite(site.slug)}>Open site</button> : null}
                  {!hasAdminAccess && !site.platform_grant_id ? <button type="button" style={buttonStyle} onClick={() => setSelectedSite(site)}>Record approval and add access</button> : null}
                  {site.platform_grant_id ? (
                    <button type="button" disabled={busy} style={buttonStyle} onClick={() => {
                      if (window.confirm(`Remove your platform-granted access to ${site.name}?`)) {
                        void run(async () => { await api.revokePlatformSiteAccess(site.id); await onAccessChanged() })
                      }
                    }}>Remove my access</button>
                  ) : null}
                </div>
              </div>
            )
          })}
        </section>
      ))}
      {role === 'founder' ? (
        <section style={{ ...cardStyle, display: 'grid', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 19 }}>Sales and admin access</h2>
          <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 13 }}>Add an existing FuseGrid login to this directory. Each person still records site owner approval before entering a site.</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input aria-label="Staff login email" type="email" value={staffEmail} onChange={(event) => setStaffEmail(event.target.value)} placeholder="person@example.com" style={{ padding: 9, minWidth: 240 }} />
            <select aria-label="Staff role" value={staffRole} onChange={(event) => setStaffRole(event.target.value as 'founder' | 'sales' | 'admin')}><option value="sales">Sales</option><option value="admin">Admin</option><option value="founder">Founder (Casey only)</option></select>
            <button type="button" disabled={busy || !staffEmail.trim()} style={buttonStyle} onClick={() => void run(async () => { await api.grantPlatformStaff(staffEmail, staffRole); setStaffEmail('') })}>Grant directory access</button>
          </div>
          {staff.map((person) => (
            <div key={person.user_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid var(--fg-border-2)', paddingTop: 8 }}>
              <span>{person.email} · {person.role}</span>
              {person.role !== 'founder' ? <button type="button" disabled={busy} style={buttonStyle} onClick={() => {
                if (window.confirm(`Remove ${person.email} from platform staff?`)) void run(async () => { await api.revokePlatformStaff(person.user_id) })
              }}>Remove</button> : null}
            </div>
          ))}
        </section>
      ) : null}
      {selectedSite ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(18,31,43,.55)', display: 'grid', placeItems: 'center', padding: 16 }}>
          <div role="dialog" aria-modal="true" aria-label={`Add access to ${selectedSite.name}`} style={{ ...cardStyle, width: 'min(520px, 100%)', display: 'grid', gap: 12 }}>
            <h2 style={{ margin: 0 }}>Add access to {selectedSite.name}</h2>
            <p style={{ margin: 0 }}>Record the site owner’s verbal approval. This grants your account site admin access; it does not grant tenant admin access.</p>
            {error ? <div role="alert" style={{ color: '#a83e29' }}>{error}</div> : null}
            <label style={{ display: 'grid', gap: 5 }}>Approving site owner’s name<input value={approvedBy} onChange={(event) => setApprovedBy(event.target.value)} /></label>
            <label style={{ display: 'grid', gap: 5 }}>Date approval received<input type="date" max={todayLocal()} value={approvalDate} onChange={(event) => setApprovalDate(event.target.value)} /></label>
            <label style={{ display: 'grid', gap: 5 }}>Context (optional)<textarea value={approvalNote} onChange={(event) => setApprovalNote(event.target.value)} rows={3} /></label>
            <label><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> I confirm that the site owner verbally approved my access.</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={buttonStyle} disabled={busy || !confirmed || !approvedBy.trim() || !approvalDate} onClick={() => void grant()}>Record approval and add access</button>
              <button type="button" style={buttonStyle} disabled={busy} onClick={() => setSelectedSite(null)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
      {selectedTrialTenant ? (
        <dialog ref={trialDialogRef} role="dialog" aria-modal="true" aria-label={`Extend trial for ${selectedTrialTenant.name}`} className="fg-platform-trial-dialog" onCancel={(event) => { event.preventDefault(); if (!trialBusyRef.current) setSelectedTrialTenant(null) }} style={{ ...cardStyle, padding: 0, color: 'var(--fg-shell-text)' }}>
          <form onSubmit={(event) => { event.preventDefault(); void extendTrial() }} style={{ padding: 16, display: 'grid', gap: 14 }}>
            <h2 style={{ margin: 0 }}>Extend trial for {selectedTrialTenant.name}</h2>
            <p style={{ margin: 0, color: 'var(--fg-muted)' }}>Extends this company’s application trial through the new date. No charge is created.</p>
            <div style={{ display: 'grid', gap: 5, padding: 12, borderRadius: 7, background: 'var(--fg-surface-2, #f5f7f6)' }}>
              <span>Current end: <strong>{trialDate(selectedTrialTenant.trial_ends_at)}</strong></span>
              <span>New end: <strong>{trialDate(trialEnd || null)}</strong></span>
              <small style={{ color: 'var(--fg-muted)' }}>Dates are shown in your local time.</small>
            </div>
            <div role="group" aria-label="Add trial days" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[7, 14, 30].map((days) => <button key={days} type="button" aria-pressed={trialPreset === days} disabled={busy} style={{ ...buttonStyle, borderColor: trialPreset === days ? 'var(--fg-accent, #245d70)' : 'var(--fg-shell-border)' }} onClick={() => { setTrialEnd(extendedTrialEnd(selectedTrialTenant, days)); setTrialPreset(days) }}>+{days} days</button>)}
            </div>
            <small style={{ color: 'var(--fg-muted)' }}>Days are added to the current end, or to today if the trial has expired.</small>
            <label style={{ display: 'grid', gap: 5 }}>New trial end<input type="datetime-local" value={localDateTime(trialEnd)} disabled={busy} onChange={(event) => { const value = new Date(event.target.value); setTrialEnd(Number.isFinite(value.getTime()) ? value.toISOString() : ''); setTrialPreset(null) }} /></label>
            {!trialEndValid ? <span style={{ color: '#a83e29', fontSize: 13 }}>Choose a date later than the current trial end and today, within the next year.</span> : null}
            <label style={{ display: 'grid', gap: 5 }}>Reason for extension<textarea value={trialReason} maxLength={2000} disabled={busy} onChange={(event) => setTrialReason(event.target.value)} placeholder="For example, additional time after a service outage" rows={3} /></label>
            {trialError ? <div role="alert" style={{ color: '#a83e29' }}>{trialError}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" style={buttonStyle} disabled={busy} onClick={() => setSelectedTrialTenant(null)}>Cancel</button>
              <button type="submit" style={buttonStyle} disabled={busy || !trialEndValid || !trialReason.trim() || trialReason.trim().length > 2000}>{busy ? 'Saving…' : 'Confirm trial extension'}</button>
            </div>
          </form>
        </dialog>
      ) : null}
    </main>
  )
}
